import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";
import { useApp } from "@/context/AppStore";
import { api } from "@/lib/api";
import { Panel, AnimatedNumber } from "@/components/common/Widgets";
import { SupplierQuadrant } from "@/components/SupplierQuadrant";
import { SupplierLeaderboard } from "@/components/SupplierLeaderboard";
import { fmtNum, classColor } from "@/lib/format";

export default function CoalIntelligence() {
  const { snapshot } = useApp();
  const [trend, setTrend] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [quadrant, setQuadrant] = useState([]);
  const [selected, setSelected] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const load = () => api.coalTrend(144).then(setTrend).catch(() => {});
    load();
    api.suppliers().then((s) => { setSuppliers(s); setSelected((cur) => cur || s[0]?.id); }).catch(() => {});
    api.supplierQuadrant().then(setQuadrant).catch(() => {});
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (selected) api.supplier(selected).then(setProfile).catch(() => {});
  }, [selected]);

  if (!snapshot) return <div className="p-10 text-ink-soft font-mono">Loading…</div>;
  const c = snapshot.coal;
  const trendData = trend.map((d) => ({ ...d, ts: new Date(d.t).getTime() }));
  const tickFmt = (ts) => {
    const dt = new Date(ts);
    return `${dt.getHours()}:${String(dt.getMinutes()).padStart(2, "0")}`;
  };

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-4 animate-fade-up" data-testid="coal-intelligence">
      <div>
        <div className="eyebrow">Coal Intelligence Center</div>
        <h1 className="font-display font-extrabold text-2xl uppercase tracking-tight text-ink">Live Quality · Supplier Performance</h1>
      </div>

      {/* NIR live cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="nir-cards">
        <NirCard label="GCV" value={c.gcv} unit="kcal/kg" color="#33b76d" />
        <NirCard label="Effective GCV" value={c.effective_gcv} unit="kcal/kg" color="#1d6fb8" />
        <NirCard label="Moisture" value={c.moisture} unit="%" decimals={1} color={c.moisture > 17 ? "#E63946" : "#fab20b"} />
        <NirCard label="Ash" value={c.ash} unit="%" decimals={1} color="#fab20b" />
        <NirCard label="Sulphur" value={c.sulphur} unit="%" decimals={2} color="#fab20b" />
        <NirCard label="Quality Index" value={c.quality_index} unit="/100" decimals={1} color="#33b76d" />
      </div>
      <div className="text-[0.66rem] font-mono text-ink-soft flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-brand-green live-dot" /> NIR feed · {c.supplier} · updated every minute · Variability ±{fmtNum(c.variability, 1)}
      </div>

      {/* trend */}
      <Panel eyebrow="Last 24 Hours" title="Quality Trend (NIR)" testid="quality-trend">
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData} margin={{ top: 8, right: 12, bottom: 0, left: -16 }}>
              <CartesianGrid stroke="rgba(139,161,192,0.08)" />
              <XAxis dataKey="ts" type="number" scale="time" domain={["dataMin", "dataMax"]} tickFormatter={tickFmt}
                tick={{ fill: "#8BA1C0", fontSize: 10, fontFamily: "JetBrains Mono" }} minTickGap={50} stroke="#1c2c4d" />
              <YAxis yAxisId="l" tick={{ fill: "#8BA1C0", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#1c2c4d" />
              <YAxis yAxisId="r" orientation="right" tick={{ fill: "#8BA1C0", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#1c2c4d" />
              <Tooltip contentStyle={{ background: "#0A1224", border: "1px solid rgba(139,161,192,0.3)", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <Line yAxisId="l" type="monotone" dataKey="gcv" name="GCV" stroke="#33b76d" dot={false} strokeWidth={1.8} />
              <Line yAxisId="r" type="monotone" dataKey="moisture" name="Moisture %" stroke="#fab20b" dot={false} strokeWidth={1.8} />
              <Line yAxisId="r" type="monotone" dataKey="ash" name="Ash %" stroke="#E63946" dot={false} strokeWidth={1.4} strokeDasharray="4 3" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      {/* quadrant + leaderboard */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel eyebrow="Value vs Risk" title="Supplier Performance Map">
          <SupplierQuadrant data={quadrant} onSelect={setSelected} />
        </Panel>
        <Panel eyebrow="F1 Standings" title="Supplier Leaderboard">
          <SupplierLeaderboard data={suppliers} onSelect={setSelected} selectedId={selected} />
        </Panel>
      </div>

      {/* supplier profile */}
      {profile && <SupplierProfile p={profile} />}
    </div>
  );
}

function NirCard({ label, value, unit, color, decimals = 0 }) {
  return (
    <div className="panel px-3.5 py-3" data-testid={`nir-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="eyebrow truncate">{label}</div>
      <div className="flex items-baseline gap-1 mt-1.5">
        <span className="text-xl font-bold tnum" style={{ color }}><AnimatedNumber value={value} decimals={decimals} /></span>
        <span className="text-[0.64rem] text-ink-soft font-mono">{unit}</span>
      </div>
    </div>
  );
}

function SupplierProfile({ p }) {
  const scores = p.scores;
  return (
    <Panel eyebrow="Supplier Profile" title={p.name} testid="supplier-profile"
      right={<span className="text-[0.66rem] font-bold uppercase tracking-wider font-mono px-2 py-1" style={{ color: classColor(p.classification), border: `1px solid ${classColor(p.classification)}55` }}>{p.classification} · SPI {p.spi}</span>}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Metric label="Steam Yield" value={`${fmtNum(p.steam_yield, 2)}`} unit="t/t" />
        <Metric label="Effective GCV" value={fmtNum(p.effective_gcv)} unit="kcal/kg" />
        <Metric label="Landed Cost" value={`₹ ${fmtNum(p.landed_cost)}`} unit="/MT" />
        <Metric label="Carbon Intensity" value={p.carbon_intensity} />
      </div>
      <div className="space-y-2">
        {Object.entries(scores).map(([k, v]) => (
          <div key={k} className="flex items-center gap-3">
            <span className="text-[0.7rem] font-mono text-ink-soft w-28 capitalize">{k.replace("_", " ")}</span>
            <div className="flex-1 h-2 bg-[#16223d]">
              <div className="h-full" style={{ width: `${v}%`, background: v >= 75 ? "#33b76d" : v >= 55 ? "#fab20b" : "#E63946" }} />
            </div>
            <span className="text-[0.72rem] font-mono tnum text-ink w-9 text-right">{fmtNum(v)}</span>
          </div>
        ))}
      </div>
      {p.rank === 1 && (
        <div className="mt-4 panel-2 px-3 py-2 border-l-2 text-[0.8rem] text-ink" style={{ borderLeftColor: "#33b76d" }}>
          <span className="text-brand-green font-bold">Recommendation:</span> Preferred Supplier — highest blended value, lowest risk.
        </div>
      )}
    </Panel>
  );
}

function Metric({ label, value, unit }) {
  return (
    <div className="panel-2 px-3 py-2.5">
      <div className="eyebrow truncate">{label}</div>
      <div className="flex items-baseline gap-1 mt-0.5">
        <span className="text-base font-bold tnum text-ink">{value}</span>
        {unit && <span className="text-[0.62rem] text-ink-soft font-mono">{unit}</span>}
      </div>
    </div>
  );
}
